import { AuthenticatorProps, CheckboxField, useAuthenticator } from "@aws-amplify/ui-react";
import { Button } from "../components/ui/button";
import { fetchSignupAvailability, useSignupAvailability } from "../hooks/useSignupAvailability";

const TERMS_URL = "/terms";
const PRIVACY_URL = "https://www.nicnet.co.jp/privacy/index.html";

const params: AuthenticatorProps = {
  components: {
    Header: () => (
      <div className="mb-6 px-4 pt-8 text-center">
        <img src="/logo-type.svg" alt="Refloop" className="mx-auto h-8 opacity-70" />
        <p className="mt-2 text-sm text-muted-foreground">ログインしてご利用ください</p>
      </div>
    ),
    SignIn: {
      Footer: () => {
        const { toSignUp, toForgotPassword } = useAuthenticator();
        const { isSignupAvailable, loading } = useSignupAvailability();
        return (
          <div className="mt-2 px-4 text-center">
            <Button variant="link" className="text-sm underline" onClick={toForgotPassword}>
              パスワードをお忘れですか？
            </Button>
            <p className="mt-4 text-sm text-muted-foreground">
              各種お問合せはLINE公式アカウントにて受け付けております
            </p>
            {!loading && !isSignupAvailable && (
              <p className="mt-4 text-xs text-muted-foreground">
                只今登録ユーザー急増により、新規受付を一時停止しております。
                <br />
                受付再開の際には、公式LINEにてご案内いたします。
              </p>
            )}
            {!loading && isSignupAvailable && (
              <Button variant="outline" className="mt-4 w-full rounded-full" onClick={toSignUp}>
                初回登録はこちら
              </Button>
            )}
          </div>
        );
      }
    },
    SignUp: {
      Header: () => {
        const { isSignupAvailable, loading } = useSignupAvailability();

        if (loading) return null;
        if (!isSignupAvailable) {
          return (
            <div className="mx-6 mb-4 rounded-md bg-destructive/10 p-4 text-center text-sm text-destructive">
              現在新規登録を受け付けておりません
            </div>
          );
        }
        return null;
      },
      FormFields: () => {
        const { isSignupAvailable } = useSignupAvailability();

        return (
          <>
            <Authenticator.SignUp.FormFields />

            <CheckboxField
              name="terms"
              value="yes"
              label={
                <>
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    利用規約
                  </a>
                  に同意する
                </>
              }
              isDisabled={!isSignupAvailable}
            />
            <CheckboxField
              name="privacy"
              value="yes"
              label={
                <>
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    プライバシーポリシー
                  </a>
                  に同意する
                </>
              }
              isDisabled={!isSignupAvailable}
            />

            <p className="mt-2 text-xs text-muted-foreground">
              東海エリアに特化しているため、愛知/岐阜/三重/静岡に在住の方のみのご利用となります
            </p>
          </>
        );
      },
      Footer: () => {
        const { toSignIn } = useAuthenticator();
        return (
          <div className="mt-4 px-4 text-center">
            <Button variant="link" className="text-sm" onClick={toSignIn}>
              ログインに戻る
            </Button>
          </div>
        );
      }
    },
    ConfirmSignUp: {
      Header: () => (
        <div className="mx-6 mb-4 text-center text-sm text-muted-foreground">
          メールに送信された確認コードを入力してください
        </div>
      )
    }
  },
  loginMechanisms: ["email"],
  hideSignUp: false,
  formFields: {
    signIn: {
      username: {
        label: "メールアドレス",
        placeholder: "メールアドレスを入力"
      },
      password: {
        label: "パスワード (小文字、数字、記号を含む10文字以上)",
        placeholder: "パスワードを入力"
      }
    },
    signUp: {
      email: {
        label: "メールアドレス",
        placeholder: "メールアドレスを入力",
        order: 1
      },
      password: {
        label: "パスワード (小文字、数字、記号を含む10文字以上)",
        placeholder: "パスワードを入力",
        order: 2
      },
      confirm_password: {
        label: "パスワードの確認",
        placeholder: "パスワードを再入力してください",
        order: 3
      }
    }
  },
  services: {
    async validateCustomSignUp(formData) {
      const errors: Record<string, string> = {};
      if (formData.terms !== "yes") {
        errors.terms = "利用規約への同意が必要です";
      }
      if (formData.privacy !== "yes") {
        errors.privacy = "プライバシーポリシーへの同意が必要です";
      }
      if (Object.keys(errors).length > 0) {
        return errors;
      }
      return undefined;
    },
    async handleSignUp(input) {
      const isSignupAvailable = await fetchSignupAvailability();
      if (!isSignupAvailable) {
        throw new Error("現在新規登録を受け付けておりません");
      }
      const { signUp } = await import("aws-amplify/auth");
      return signUp(input);
    }
  }
};

// Authenticator を FormFields 内で参照するための import
// (循環参照を避けるため遅延的に参照)
import { Authenticator } from "@aws-amplify/ui-react";

export default params;
